import { computed } from 'vue';
import type { Ref } from 'vue';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/vue-query';
import {
  getRelatiesGetOverzichtCountQueryKey,
  getRelatiesGetOverzichtQueryKey,
  getRelatiesGetOverzichtUitgebreidCountQueryKey,
  getRelatiesGetOverzichtUitgebreidQueryKey,
  useRelatiesGetOverzicht,
  useRelatiesGetOverzichtCount,
  useRelatiesGetOverzichtUitgebreid,
  useRelatiesGetOverzichtUitgebreidCount,
} from '@services/crm-public/orval-api/relaties-api/relaties-api';
import type { RelatiesGetOverzichtParams } from '@services/crm-public/orval-api/model/relatiesGetOverzichtParams';
import type { RelatiesGetOverzichtCountParams } from '@services/crm-public/orval-api/model/relatiesGetOverzichtCountParams';
import type { RelatiesGetOverzichtUitgebreidParams } from '@services/crm-public/orval-api/model/relatiesGetOverzichtUitgebreidParams';
import type { RelatiesGetOverzichtUitgebreidCountParams } from '@services/crm-public/orval-api/model/relatiesGetOverzichtUitgebreidCountParams';
import type { SUREbusinessCCSPublisherFeaturesRelatieFindRelatiesDto } from '@services/crm-public/orval-api/model/sUREbusinessCCSPublisherFeaturesRelatieFindRelatiesDto';
import apiService from '@services/base/apiService';
import { useFeatureToggle } from '@environment/useFeatureToggle';
import type { Relatie, RelatieResponse, SearchParams } from './types';

export const relatieStatus = {
  None: 0,
  Prospect: 1,
  Beeindigd: 2,
  Actief: 3,
} as const;

export interface RelatiesOverzichtOptions {
  naamNummer: Ref<string>;
  limit: Ref<number>;
  offset: Ref<number>;
  isMuterenEnRoyeren: Ref<boolean>;
  searchParams: Ref<SearchParams>;
  adviseurId: Ref<number | null | undefined>;
}

function voegQueryParameterToe(
  query: string,
  key: string,
  value: number | string | undefined,
): string {
  if (value) {
    return `${query}&${key}=${value}`;
  }
  return query;
}

function mapNaarRelatie(item: SUREbusinessCCSPublisherFeaturesRelatieFindRelatiesDto): Relatie {
  return {
    RelatieNummer: item.relatienummer,
    RelatieNaam: item.achternaam,
    TypeRelatie: item.soort as 'P' | 'Z',
    Adviseurnaam: item.naamAdviseur,
    Adviseurnummer: item.adviseurnummer,
    StatusNaam: item.status,
    Adres: item.straat,
    Plaats: item.woonplaats,
  };
}

/**
 * Voor het overzicht gebruiken we een nieuw endpoint (betere performance). Voor uitgebreid zoeken nog een oud (.NET Framework) endpoint.
 * Achter de toggle RelatiesUitgebreidZoekenViaManagementToggle gaat ook het uitgebreid zoeken naar een nieuw endpoint;
 * met de toggle uit blijft het oude endpoint het uitgebreid zoeken doen. Beide paden kennen alle negen zoekvelden.
 */
export function useRelatiesOverzicht(options: RelatiesOverzichtOptions) {
  const { naamNummer, limit, offset, isMuterenEnRoyeren, searchParams, adviseurId } = options;
  const queryClient = useQueryClient();

  const uitgebreidViaManagement = useFeatureToggle('RelatiesUitgebreidZoekenViaManagementToggle');

  const isUitgebreidZoeken = computed(() =>
    Object.values(searchParams.value).some((v) => !!v && v.length > 0),
  );

  // Zolang de toggle-waarde nog laadt (undefined) kiest dit bewust het legacy-endpoint: toggle uit = huidig gedrag.
  const gebruikLegacyEndpoint = computed(
    () => isUitgebreidZoeken.value && uitgebreidViaManagement.value !== true,
  );

  const gebruikManagementEndpoint = computed(
    () => isUitgebreidZoeken.value && uitgebreidViaManagement.value === true,
  );

  const uitgebreidLegacyUrl = computed(() => {
    const status = isMuterenEnRoyeren.value ? relatieStatus.Actief : relatieStatus.None;
    let endpoint = `/net/relation/search?aantal=${limit.value}&offset=${offset.value}&naamOfNummer=${naamNummer.value}&status=${status}`;
    endpoint = voegQueryParameterToe(endpoint, 'adviseurId', adviseurId.value ?? undefined);
    endpoint = voegQueryParameterToe(endpoint, 'postcode', searchParams.value.postcode);
    endpoint = voegQueryParameterToe(endpoint, 'kvknummer', searchParams.value.kvknummer);
    endpoint = voegQueryParameterToe(endpoint, 'email', searchParams.value.email);
    endpoint = voegQueryParameterToe(endpoint, 'contactpersoon', searchParams.value.contactpersoon);
    endpoint = voegQueryParameterToe(endpoint, 'polisnummer', searchParams.value.polisnummer);
    endpoint = voegQueryParameterToe(endpoint, 'kenteken', searchParams.value.kenteken);
    endpoint = voegQueryParameterToe(endpoint, 'factuurnummer', searchParams.value.factuurnummer);
    endpoint = voegQueryParameterToe(endpoint, 'schadenummer', searchParams.value.schadenummer);
    endpoint = voegQueryParameterToe(endpoint, 'iban', searchParams.value.iban);
    return endpoint;
  });

  const uitgebreidLegacyQueryKey = computed(() => [
    'net',
    'relation',
    'search',
    offset.value,
    naamNummer.value,
    adviseurId.value,
    searchParams.value.postcode,
    searchParams.value.kvknummer,
    searchParams.value.email,
    searchParams.value.contactpersoon,
    searchParams.value.polisnummer,
    searchParams.value.kenteken,
    searchParams.value.factuurnummer,
    searchParams.value.schadenummer,
    searchParams.value.iban,
  ]);

  const {
    data: uitgebreidLegacyData,
    isLoading: uitgebreidLegacyIsLoading,
    isFetching: uitgebreidLegacyIsFetching,
    isError: uitgebreidLegacyIsError,
    error: uitgebreidLegacyError,
  } = useQuery({
    queryKey: uitgebreidLegacyQueryKey,
    queryFn: () => apiService.apiClient.get<RelatieResponse>(uitgebreidLegacyUrl.value),
    select: (r) => r.data,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
    enabled: gebruikLegacyEndpoint,
  });

  const uitgebreidCountParams = computed<RelatiesGetOverzichtUitgebreidCountParams>(() => ({
    naamNummer: naamNummer.value,
    enkelActieve: isMuterenEnRoyeren.value,
    adviseurnummer: adviseurId.value ?? undefined,
    Postcode: searchParams.value.postcode || undefined,
    Emailadres: searchParams.value.email || undefined,
    Contactpersoon: searchParams.value.contactpersoon || undefined,
    Polisnummer: searchParams.value.polisnummer || undefined,
    Kenteken: searchParams.value.kenteken || undefined,
    Schadenummer: searchParams.value.schadenummer || undefined,
    Iban: searchParams.value.iban || undefined,
    Kvknummer: searchParams.value.kvknummer || undefined,
    Factuurnummer: searchParams.value.factuurnummer || undefined,
  }));

  // Lijst = count + paginering.
  const uitgebreidParams = computed<RelatiesGetOverzichtUitgebreidParams>(() => ({
    ...uitgebreidCountParams.value,
    limit: limit.value,
    offset: offset.value,
  }));

  const {
    data: uitgebreidData,
    isPending: uitgebreidIsPending,
    isFetching: uitgebreidIsFetching,
    isError: uitgebreidIsError,
    error: uitgebreidError,
  } = useRelatiesGetOverzichtUitgebreid(uitgebreidParams, {
    query: {
      select: (r) => r.result,
      enabled: gebruikManagementEndpoint,
      placeholderData: keepPreviousData,
      staleTime: 60 * 1000,
    },
  });

  const { data: uitgebreidCountData } = useRelatiesGetOverzichtUitgebreidCount(
    uitgebreidCountParams,
    {
      query: {
        select: (r) => r.result,
        enabled: gebruikManagementEndpoint,
        staleTime: 60 * 1000,
      },
    },
  );

  const overzichtParams = computed<RelatiesGetOverzichtParams>(() => ({
    naamNummer: naamNummer.value,
    adviseurnummer: adviseurId.value ?? undefined,
    limit: limit.value,
    enkelActieve: isMuterenEnRoyeren.value,
    offset: offset.value,
  }));

  const countParams = computed<RelatiesGetOverzichtCountParams>(() => ({
    naamNummer: naamNummer.value,
    enkelActieve: isMuterenEnRoyeren.value,
    adviseurnummer: adviseurId.value ?? undefined,
  }));

  const {
    data: overzichtData,
    isPending: overzichtIsPending,
    isFetching: overzichtIsFetching,
    isError: overzichtIsError,
    error: overzichtError,
  } = useRelatiesGetOverzicht(overzichtParams, {
    query: {
      select: (r) => r.result,
      enabled: computed(() => !isUitgebreidZoeken.value),
      placeholderData: keepPreviousData,
      staleTime: 60 * 1000,
    },
  });

  const { data: countData } = useRelatiesGetOverzichtCount(countParams, {
    query: {
      select: (r) => r.result,
      enabled: computed(() => !isUitgebreidZoeken.value),
      staleTime: 60 * 1000,
    },
  });

  // Drie bronnen met hetzelfde vlak; alleen actieveBron weet welke geldt.
  const bronUitgebreidLegacy = {
    relaties: computed<Relatie[]>(() => uitgebreidLegacyData.value?.items ?? []),
    totalItems: computed<number>(() => uitgebreidLegacyData.value?.totalItems ?? 0),
    isFetching: uitgebreidLegacyIsFetching,
    isLoading: uitgebreidLegacyIsLoading,
    isError: uitgebreidLegacyIsError,
    error: uitgebreidLegacyError,
    invalideer: () => {
      queryClient.invalidateQueries({ queryKey: uitgebreidLegacyQueryKey.value });
    },
  };

  const bronUitgebreid = {
    relaties: computed<Relatie[]>(() => (uitgebreidData.value ?? []).map(mapNaarRelatie)),
    totalItems: computed<number>(() => Number(uitgebreidCountData.value ?? 0)),
    isFetching: uitgebreidIsFetching,
    isLoading: uitgebreidIsPending,
    isError: uitgebreidIsError,
    error: uitgebreidError,
    invalideer: () => {
      queryClient.invalidateQueries({
        queryKey: getRelatiesGetOverzichtUitgebreidQueryKey(uitgebreidParams),
      });
      queryClient.invalidateQueries({
        queryKey: getRelatiesGetOverzichtUitgebreidCountQueryKey(uitgebreidCountParams),
      });
    },
  };

  const bronOverzicht = {
    relaties: computed<Relatie[]>(() => (overzichtData.value ?? []).map(mapNaarRelatie)),
    totalItems: computed<number>(() => Number(countData.value ?? 0)),
    isFetching: overzichtIsFetching,
    isLoading: overzichtIsPending,
    isError: overzichtIsError,
    error: overzichtError,
    invalideer: () => {
      queryClient.invalidateQueries({ queryKey: getRelatiesGetOverzichtQueryKey(overzichtParams) });
      queryClient.invalidateQueries({
        queryKey: getRelatiesGetOverzichtCountQueryKey(countParams),
      });
    },
  };

  const actieveBron = computed(() => {
    if (gebruikLegacyEndpoint.value) return bronUitgebreidLegacy;
    if (gebruikManagementEndpoint.value) return bronUitgebreid;
    return bronOverzicht;
  });

  return {
    relaties: computed(() => actieveBron.value.relaties.value),
    totalItems: computed(() => actieveBron.value.totalItems.value),
    isFetching: computed(() => actieveBron.value.isFetching.value),
    isLoading: computed(() => actieveBron.value.isLoading.value),
    isError: computed(() => actieveBron.value.isError.value),
    error: computed(() => actieveBron.value.error.value),
    isUitgebreidZoeken,
    uitgebreidViaManagement,
    invalideerCache: () => actieveBron.value.invalideer(),
  };
}
